/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import {
    ACTION_VIEW,
    ConfiguredGrid,
    RequestGridSortDirections,
    SortingMode,
    useGlobalAddons,
    useGlobalLang,
} from '@veeam-vspc/shared/components';
import { capitalize } from '@veeam-vspc/shared/helpers';

import type { BackupServerBackupJobIndexingSettings } from '@veeam-vspc/models/rest';
import type { GridStore, RequestParams } from '@veeam-vspc/shared/components';
import type { RequestSuccessResponse } from '@veeam-vspc/shared/interfaces';
import type { VspcLang } from 'configs/languages';

import { SidePanelForm } from 'components/layouts/SidePanelForm';
import { ActionsToolbar } from './components/ActionToolbar/ActionsToolbar';
import { useJobWizardStore } from '../../../../stores';

interface FileSystemIndexingOptionsProps {
    hidePanel: () => void;
}

export const FileSystemIndexingOptions: React.FC<FileSystemIndexingOptionsProps> = observer(({ hidePanel }) => {
    const lang = useGlobalLang<VspcLang>();
    const api = useRef<GridStore<BackupServerBackupJobIndexingSettings, unknown, unknown>>();
    const wizardStore = useJobWizardStore();
    const { portalEnums } = useGlobalAddons();

    const fetchData = (
        { page, limit, sort, filter }: RequestParams<BackupServerBackupJobIndexingSettings & { name: string; }>,
    ): Promise<RequestSuccessResponse<BackupServerBackupJobIndexingSettings[]>> => {
        let items = wizardStore.guestFSIndexingSettings.slice();

        if (sort && sort.length) {
            items.sort((a, b) => {
                const { direction, property } = sort[0];

                let aValue = String(a[property]);
                let bValue = String(b[property]);

                if (property === 'name') {
                    aValue = a.vmObject.name;
                    bValue = b.vmObject.name;
                }

                if (direction === RequestGridSortDirections.Asc) {
                    return aValue
                        .localeCompare(
                            bValue,
                            'kn',
                            { sensitivity: 'base', numeric: true }
                        );
                }

                if (direction === RequestGridSortDirections.Desc) {
                    return bValue
                        .localeCompare(
                            aValue,
                            'kn',
                            { sensitivity: 'base', numeric: true }
                        );
                }

                return 0;
            });
        }

        items = items
            .filter(x => x.vmObject.name
                .toLowerCase()
                .includes((filter.name || '').trim().toLowerCase()),
            );

        const chunk = items.slice((page * limit - limit), page * limit);

        return Promise.resolve({
            data: chunk.map(x => ({ ...x, objectId: x.vmObject.objectId })),
            meta: {
                pagingInfo: {
                    total: items.length,
                },
            },
        });
    };

    return (
        <SidePanelForm
            onRequestClose={hidePanel}
            title={capitalize(lang.GUEST_FILE_SYSTEM_INDEXING_OPTIONS)}
            description={`${lang.SPECIFY_GUEST_FILE_SYSTEM_INDEXING_SETTINGS_FOR_INDIVIDUAL_OBJECTS}.`}
            actions={[
                {
                    text: lang.APPLY,
                    onClick: () => {
                        const data = wizardStore.data;
                        data.guestProcessing.guestFSIndexing.indexingSettings = Array.from(wizardStore.guestFSIndexingSettings);

                        wizardStore.updateJobData(data);

                        hidePanel();
                    },
                },
                {
                    text: lang.CANCEL,
                    view: ACTION_VIEW.secondary,
                    onClick() {
                        wizardStore.handleUpdateGuestFSIndexingSettings(wizardStore.data.guestProcessing?.guestFSIndexing?.indexingSettings ?? []);

                        hidePanel();
                    },
                },
            ]}
        >
            <ConfiguredGrid
                stateId='backupJobs.VMs.virtualInfrastructure.jobWizard.guestProcessing.FileSystemIndexingOptions'
                defaultColumnMinWidth={100}
                columns={[
                    {
                        id: 'name',
                        title: lang.OBJECT,
                        cell: rowData => rowData.vmObject.name,
                    },
                    {
                        id: 'windowsIndexing',
                        title: lang.WINDOWS,
                        cell: rowData => portalEnums
                            .getEnumDescription('BackupServerBackupJobGuestFSIndexingMode', rowData.windowsIndexing.guestFSIndexingMode),
                    },
                    {
                        id: 'linuxIndexing',
                        title: lang.LINUX,
                        cell: rowData => portalEnums
                            .getEnumDescription('BackupServerBackupJobGuestFSIndexingMode', rowData.linuxIndexing.guestFSIndexingMode),
                    },
                ]}
                data={fetchData}
                toggleable={false}
                disableAutoUpdate={true}
                selection={{
                    field: 'objectId',
                    multiple: false,
                    checkbox: false,
                }}
                toolbars={[
                    () => <ActionsToolbar />,
                ]}
                api={api}
                sortingMode={SortingMode.Remote}
            />
        </SidePanelForm>
    );
});
